import { Box, Paper } from '@mui/material';
import type { FormDefinition } from '../../format/form-definition';
import { useBuilder } from '../context';
import { CanvasElements } from './CanvasElements';
import { FormHeader } from '../../rendering/FormElements';
import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from 'framework/ui/mui-builder';

interface CanvasProps {
  onOpenSettings: () => void;
  onEmptyClick: () => void;
  scrollTo: string | null;
  footer?: ReactNode;
}

export const Canvas = ({ onOpenSettings, onEmptyClick, scrollTo, footer }: CanvasProps) => {
  const { form, drawer } = useBuilder();
  const canvas = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const scroller = canvas.current;
    const element = scrollTo && scroller?.querySelector(`[data-element-id="${CSS.escape(scrollTo)}"]`);
    if (!scroller || !element) return;
    const view = scroller.getBoundingClientRect();
    const box = element.getBoundingClientRect();
    const far = box.top > view.bottom + view.height || box.bottom < view.top - view.height;
    element.scrollIntoView({ block: 'nearest', behavior: far ? 'instant' : 'smooth' });
  }, [scrollTo]);

  return (
    <Box
      ref={canvas}
      onClick={onEmptyClick}
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        overflow: 'auto',
        scrollbarGutter: 'stable',
        isolation: 'isolate',
        pb: 4,
      }}
    >
      <Paper variant="outlined" sx={{ p: 4 }}>
        <FormTitle form={form} selected={drawer?.kind === 'form'} onOpen={onOpenSettings} />
        <Box sx={{ mt: 2.5 }}>
          <CanvasElements elements={form.elements} container={null} />
        </Box>
      </Paper>
      {footer && <Box sx={{ mx: 'auto', mt: 2 }}>{footer}</Box>}
    </Box>
  );
};

const FormTitle = ({ form, selected, onOpen }: { form: FormDefinition; selected: boolean; onOpen: () => void }) => {
  return (
    <Button
      component="div"
      variant="text"
      disableRipple
      onClick={event => {
        event.stopPropagation();
        onOpen();
      }}
      sx={{
        display: 'block',
        color: 'text.primary',
        font: 'inherit',
        textAlign: 'left',
        textTransform: 'none',
        borderRadius: 1,
        outline: 2,
        outlineColor: selected ? 'primary.main' : 'transparent',
        '&:hover': { backgroundColor: 'transparent', outlineColor: selected ? 'primary.main' : 'border.light' },
      }}
    >
      <FormHeader title={form.title} description={form.description} />
    </Button>
  );
};
